import { useState } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { cn } from "../lib/utils"

export function MenubarRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)

  const menus = element.children ?? []

  return (
    <div
      className={cn(
        "flex items-center gap-1 rounded-md border border-border bg-background p-1 shadow-sm",
        element.className
      )}
      data-testid={`menubar-${element.id}`}
      role="menubar"
      aria-label={element.ariaLabel ?? "Application menubar"}
    >
      {menus.map((menu) => {
        const isOpen = openMenuId === menu.id
        const subItems = menu.children ?? []
        const menuLabel = menu.text
          ? String(resolveSymbol(menu.text) ?? "")
          : menu.id

        return (
          <div key={menu.id} className="relative">
            <button
              type="button"
              className={cn(
                "px-3 py-1.5 text-sm font-medium rounded-sm transition-colors",
                isOpen
                  ? "bg-accent text-accent-foreground"
                  : "text-foreground hover:bg-accent/50"
              )}
              onClick={() => {
                setOpenMenuId(isOpen ? null : menu.id)
                dispatchEvent(menu.id, "tap", { value: menuLabel })
              }}
              role="menuitem"
              aria-expanded={isOpen}
              aria-label={menu.ariaLabel ?? menuLabel}
            >
              {menuLabel}
            </button>
            {isOpen && subItems.length > 0 && (
              <div className="absolute left-0 top-full mt-1 z-50 min-w-[160px] rounded-md border border-border bg-popover p-1 shadow-md">
                {subItems.map((item) => {
                  const label = item.text
                    ? String(resolveSymbol(item.text) ?? "")
                    : item.id
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className="relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground transition-colors"
                      onClick={() => {
                        dispatchEvent(item.id, "tap", { value: label })
                        setOpenMenuId(null)
                      }}
                      role="menuitem"
                      aria-label={item.ariaLabel ?? label}
                    >
                      {label}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
